import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Briefcase, Eye, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useHrDepartmentsQuery } from '@/client/api/hr/people';
import { useJobsQuery, useUpsertJobMutation } from '@/client/api/hr/records';
import { hasLeft, EMPLOYMENT_TYPE_LABEL, type EmploymentType, type JobPostingV2 } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { EmptyState, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

const inputClass = 'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';
export const JOB_STATUS = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
  open: { label: 'Open', className: 'bg-emerald-50 text-emerald-700' },
  on_hold: { label: 'On hold', className: 'bg-amber-50 text-amber-800' },
  closed: { label: 'Closed', className: 'bg-gray-100 text-gray-400' },
} as const;

/** Mount with a `key` per posting so the form starts from that posting. */
export function JobDialog({ job, onClose }: { job: Partial<JobPostingV2>; onClose: () => void }) {
  const upsert = useUpsertJobMutation();
  const departments = useHrDepartmentsQuery().data ?? [];
  const employees = useEmployees();
  const [form, setForm] = useState<Partial<JobPostingV2>>(job);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{job.id ? 'Edit posting' : 'New job posting'}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            upsert.mutate(
              { ...form, departmentId: form.departmentId || null, hiringManagerId: form.hiringManagerId || null },
              {
                onSuccess: () => {
                  toast.success('Saved');
                  onClose();
                },
                onError: (err) => toast.error((err as Error).message || 'Could not save'),
              },
            );
          }}
        >
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Role</span>
            <input required value={form.title ?? ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Department</span>
              <select value={form.departmentId ?? ''} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className={inputClass}>
                <option value="">None</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Hiring manager</span>
              <select value={form.hiringManagerId ?? ''} onChange={(e) => setForm({ ...form, hiringManagerId: e.target.value })} className={inputClass}>
                <option value="">None</option>
                {employees
                  .filter((e) => !hasLeft(e))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Type</span>
              <select value={form.employmentType ?? 'full_time'} onChange={(e) => setForm({ ...form, employmentType: e.target.value as EmploymentType })} className={inputClass}>
                {Object.entries(EMPLOYMENT_TYPE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Openings</span>
                <input type="number" min={1} value={form.openings ?? 1} onChange={(e) => setForm({ ...form, openings: Number(e.target.value) })} className={inputClass} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Location</span>
                <input value={form.location ?? ''} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputClass} placeholder="Accra / Remote" />
              </label>
            </div>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Description (shown publicly once open)</span>
            <textarea
              rows={6}
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Status</span>
            <select value={form.status ?? 'draft'} onChange={(e) => setForm({ ...form, status: e.target.value as JobPostingV2['status'] })} className={inputClass}>
              {Object.entries(JOB_STATUS).map(([v, s]) => (
                <option key={v} value={v}>
                  {s.label}
                </option>
              ))}
            </select>
            <span className="block text-xs text-gray-500">
              Open roles are listed publicly at feelyfeely.com/content/careers. Applications arrive here as candidates.
            </span>
          </label>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function JobPostingsPage() {
  const navigate = useNavigate();
  const jobsQ = useJobsQuery();
  const departments = useHrDepartmentsQuery().data ?? [];
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [editing, setEditing] = useState<Partial<JobPostingV2> | null>(null);
  const jobs = jobsQ.data ?? [];
  const inPipeline = (j: JobPostingV2) => (j.pipeline ? j.pipeline.applied + j.pipeline.screen + j.pipeline.interview + j.pipeline.offer : 0);

  return (
    <HrPageShell
      mock={false}
      title="Job postings"
      description="Roles we are hiring for, and how far each one's candidates have got."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Recruiting' }, { label: 'Job postings' }]}
      icon={Briefcase}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={() => setEditing({ title: '', employmentType: 'full_time', openings: 1, status: 'draft' })}>
          <Plus className="h-4 w-4" /> New posting
        </PermissionButton>
      }
      stats={[
        { key: 'open', label: 'Open roles', value: jobs.filter((j) => j.status === 'open').length },
        { key: 'pipeline', label: 'Candidates in progress', value: jobs.reduce((n, j) => n + inPipeline(j), 0) },
        { key: 'offers', label: 'Offers out', value: jobs.reduce((n, j) => n + (j.pipeline?.offer ?? 0), 0) },
        { key: 'hired', label: 'Hired', value: jobs.reduce((n, j) => n + (j.pipeline?.hired ?? 0), 0) },
      ]}
    >
      {jobsQ.isError ? (
        <EmptyState label={`Could not load postings: ${(jobsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : jobsQ.isLoading ? (
        <EmptyState label="Loading postings…" />
      ) : jobs.length === 0 ? (
        <EmptyState label="No postings yet." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">In progress</th>
                <th className="px-4 py-3 font-medium">Hired / openings</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.map((j) => (
                <tr key={j.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/human-resource/recruiting/postings/${j.id}`)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{j.title}</p>
                    <p className="text-xs text-gray-400">
                      {EMPLOYMENT_TYPE_LABEL[j.employmentType]}
                      {j.location && ` · ${j.location}`}
                      {j.postedAt && ` · posted ${formatDay(j.postedAt)}`}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{departments.find((d) => d.id === j.departmentId)?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{inPipeline(j)}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {j.pipeline?.hired ?? 0} / {j.openings}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge label={JOB_STATUS[j.status].label} className={JOB_STATUS[j.status].className} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Candidates', icon: Eye, onSelect: () => navigate(`/admin/human-resource/recruiting/postings/${j.id}`) },
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setEditing(j) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <JobDialog key={editing.id ?? 'new'} job={editing} onClose={() => setEditing(null)} />}
    </HrPageShell>
  );
}
